// ListScreen.jsx - Livreur Zones
import React, { useState, useEffect, useMemo } from "react";
import { Container, Row, Col, Badge, Button, Nav, Modal, Form, Alert } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import DynamicTable from "../../components/DynamicTable";
import SearchBar from "../../components/SearchBar";
import ClickBack from "../../components/buttons/ClickBack";
import { fetchAllLivreurs, fetchLivreurZones, fetchAllZones, fetchZoneLivreurs, assignZoneToLivreur } from "./dataService";
import IconEdit from "../../images/edit.png";
import IconShow from "../../images/fi_eye.png";

function ListScreen() {
  const [viewMode, setViewMode] = useState("livreurs"); // "livreurs" or "zones"
  const [livreursData, setLivreursData] = useState([]);
  const [zonesData, setZonesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [allLivreurs, setAllLivreurs] = useState([]);
  const [allZones, setAllZones] = useState([]);
  const [newAssignment, setNewAssignment] = useState({
    livreur_id: "",
    zone_id: "",
    is_primary: false,
    priority: 1,
    notes: "",
  });
  const history = useHistory();

  useEffect(() => {
    fetchData();
    loadLivreursAndZones();
  }, [viewMode]);

  const loadLivreursAndZones = async () => {
    try {
      const [livreursResp, zonesResp] = await Promise.all([
        fetchAllLivreurs(),
        fetchAllZones(),
      ]);
      console.log("Livreurs response:", livreursResp);
      console.log("Zones response:", zonesResp);

      // Handle paginated response structure
      const livreursData = livreursResp.data?.data || livreursResp.data || [];
      const zonesData = zonesResp.data || zonesResp || [];

      console.log("Livreurs data:", livreursData);
      console.log("Zones data:", zonesData);

      setAllLivreurs(Array.isArray(livreursData) ? livreursData : []);
      setAllZones(Array.isArray(zonesData) ? zonesData : []);
    } catch (error) {
      console.error("Error loading livreurs and zones:", error);
    }
  };

  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
    setNewAssignment({
      livreur_id: "",
      zone_id: "",
      is_primary: false,
      priority: 1,
      notes: "",
    });
  };

  const handleCreateAssignment = async () => {
    if (!newAssignment.livreur_id || !newAssignment.zone_id) {
      alert("Veuillez sélectionner un livreur et une zone");
      return;
    }

    try {
      await assignZoneToLivreur(newAssignment.livreur_id, {
        zone_id: newAssignment.zone_id,
        is_primary: newAssignment.is_primary,
        priority: newAssignment.priority,
        notes: newAssignment.notes,
      });
      setShowCreateModal(false);
      alert("Assignation créée avec succès");
      fetchData(); // Refresh the list
    } catch (err) {
      console.error("Error creating assignment:", err);
      alert(err.response?.data?.message || "Erreur lors de la création de l'assignation");
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      if (viewMode === "livreurs") {
        const livreursResp = await fetchAllLivreurs();
        console.log("fetchData - Livreurs response:", livreursResp);

        // Handle paginated response structure
        const livreursArray = livreursResp.data?.data || livreursResp.data || [];
        console.log("fetchData - Livreurs array:", livreursArray);
        console.log("fetchData - Is array?", Array.isArray(livreursArray));

        // Ensure it's an array before mapping
        if (!Array.isArray(livreursArray)) {
          console.error("livreursArray is not an array:", livreursArray);
          setLivreursData([]);
          return;
        }

        // Fetch zones count for each livreur
        const livreursWithZones = await Promise.all(
          livreursArray.map(async (livreur) => {
            try {
              const zonesResp = await fetchLivreurZones(livreur.id);
              return {
                ...livreur,
                zones_count: zonesResp.zones?.length || 0,
                primary_zone: zonesResp.zones?.find(z => z.is_primary)?.zone?.name || "-",
              };
            } catch {
              return { ...livreur, zones_count: 0, primary_zone: "-" };
            }
          })
        );
        setLivreursData(livreursWithZones);
      } else {
        const zonesResp = await fetchAllZones();

        // Fetch livreurs count for each zone
        const zonesWithLivreurs = await Promise.all(
          (zonesResp || []).map(async (zone) => {
            try {
              const livreursResp = await fetchZoneLivreurs(zone.id);
              return {
                ...zone,
                livreurs_count: livreursResp.livreurs?.length || 0,
              };
            } catch {
              return { ...zone, livreurs_count: 0 };
            }
          })
        );
        setZonesData(zonesWithLivreurs);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter livreurs
  const filteredLivreurs = useMemo(() => {
    if (!searchTerm) return livreursData;
    return livreursData.filter((livreur) => {
      const fullName = `${livreur.nom} ${livreur.prenom}`.toLowerCase();
      return fullName.includes(searchTerm.toLowerCase());
    });
  }, [livreursData, searchTerm]);

  // Filter zones
  const filteredZones = useMemo(() => {
    if (!searchTerm) return zonesData;
    return zonesData.filter((zone) =>
      zone.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [zonesData, searchTerm]);

  // Livreurs columns
  const livreursColumns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
      },
      {
        Header: "Nom",
        accessor: "nom",
      },
      {
        Header: "Prénom",
        accessor: "prenom",
      },
      {
        Header: "Zone principale",
        accessor: "primary_zone",
        Cell: ({ value }) => (
          <Badge bg={value !== "-" ? "primary" : "secondary"}>{value}</Badge>
        ),
      },
      {
        Header: "Nb zones",
        accessor: "zones_count",
        Cell: ({ value }) => (
          <Badge bg={value > 0 ? "info" : "secondary"}>{value}</Badge>
        ),
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <Button
              variant="link"
              className="p-0"
              onClick={() => history.push(`/livreurs/zones/${row.original.id}/view`)}
              title="Voir les zones"
            >
              <img src={IconShow} alt="Voir" width="20" />
            </Button>
            <Button
              variant="link"
              className="p-0"
              onClick={() => history.push(`/livreurs/zones/${row.original.id}/edit`)}
              title="Modifier les zones"
            >
              <img src={IconEdit} alt="Modifier" width="20" />
            </Button>
          </div>
        ),
      },
    ],
    [history]
  );

  // Zones columns
  const zonesColumns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
      },
      {
        Header: "Nom",
        accessor: "name",
      },
      {
        Header: "Statut",
        accessor: "status",
        Cell: ({ value }) => (
          <Badge bg={value === "active" ? "success" : "secondary"}>{value || "-"}</Badge>
        ),
      },
      {
        Header: "Nb livreurs",
        accessor: "livreurs_count",
        Cell: ({ value }) => (
          <Badge bg={value > 0 ? "info" : "warning"}>{value}</Badge>
        ),
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <Button
              variant="link"
              className="p-0"
              onClick={() => history.push(`/zones/${row.original.id}/livreurs`)}
              title="Voir les livreurs"
            >
              <img src={IconShow} alt="Voir" width="20" />
            </Button>
            <Button
              variant="link"
              className="p-0"
              onClick={() => history.push(`/zones/${row.original.id}/edit`)}
              title="Gérer les livreurs"
            >
              <img src={IconEdit} alt="Modifier" width="20" />
            </Button>
          </div>
        ),
      },
    ],
    [history]
  );

  return (
    <Container fluid className="p-4">
      <Row className="mb-4">
        <Col>
          <ClickBack />
          <h2 className="mt-3">Gestion des zones de livraison</h2>
          <p className="text-muted">Assignez des zones géographiques à vos livreurs</p>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          <Nav variant="pills">
            <Nav.Item>
              <Nav.Link active={viewMode === "livreurs"} onClick={() => setViewMode("livreurs")}>
                Par livreurs
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link active={viewMode === "zones"} onClick={() => setViewMode("zones")}>
                Par zones
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </Col>
        <Col md={4} className="text-center">
          <Button variant="success" onClick={handleOpenCreateModal}>
            + Créer une assignation
          </Button>
        </Col>
        <Col md={4} className="text-end">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={
              viewMode === "livreurs"
                ? "Rechercher un livreur..."
                : "Rechercher une zone..."
            }
          />
        </Col>
      </Row>

      <Row>
        <Col>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          ) : viewMode === "livreurs" ? (
            <DynamicTable columns={livreursColumns} data={filteredLivreurs} />
          ) : (
            <DynamicTable columns={zonesColumns} data={filteredZones} />
          )}
        </Col>
      </Row>

      {/* Create Assignment Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Créer une nouvelle assignation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            Sélectionnez un livreur et une zone pour créer une nouvelle assignation.
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label>Livreur *</Form.Label>
            <Form.Select
              value={newAssignment.livreur_id}
              onChange={(e) =>
                setNewAssignment({ ...newAssignment, livreur_id: e.target.value })
              }
            >
              <option value="">Sélectionner un livreur...</option>
              {Array.isArray(allLivreurs) && allLivreurs.map((livreur) => (
                <option key={livreur.id} value={livreur.id}>
                  {livreur.nom} {livreur.prenom}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Zone *</Form.Label>
            <Form.Select
              value={newAssignment.zone_id}
              onChange={(e) =>
                setNewAssignment({ ...newAssignment, zone_id: e.target.value })
              }
            >
              <option value="">Sélectionner une zone...</option>
              {Array.isArray(allZones) && allZones.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Zone principale pour ce livreur"
              checked={newAssignment.is_primary}
              onChange={(e) =>
                setNewAssignment({ ...newAssignment, is_primary: e.target.checked })
              }
            />
            <Form.Text className="text-muted">
              Le livreur ne peut avoir qu'une seule zone principale
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Priorité</Form.Label>
            <Form.Select
              value={newAssignment.priority}
              onChange={(e) =>
                setNewAssignment({ ...newAssignment, priority: parseInt(e.target.value) })
              }
            >
              {[1, 2, 3, 4, 5].map((p) => (
                <option key={p} value={p}>
                  {p} {p === 1 && "(Haute)"} {p === 5 && "(Basse)"}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              1 = Priorité haute, 5 = Priorité basse
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={newAssignment.notes}
              onChange={(e) =>
                setNewAssignment({ ...newAssignment, notes: e.target.value })
              }
              placeholder="Notes administratives..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleCreateAssignment}>
            Créer l'assignation
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default ListScreen;
